import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPassport } from '@fortawesome/free-solid-svg-icons';
import '../styles/dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const getStatusBadgeStyle = (status) => {
  const styles = {
    'To be applied': { background: '#f3f4f6', color: '#374151' },
    'In Progress': { background: '#fef9c3', color: '#854d0e' },
    'Applied': { background: '#dbeafe', color: '#1e40af' },
    'Approved': { background: '#dcfce7', color: '#166534' },
    'Rejected': { background: '#fee2e2', color: '#991b1b' },
    'Reapplied': { background: '#ede9fe', color: '#4c1d95' },
  };
  return styles[status] || { background: '#f3f4f6', color: '#374151' };
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function MyVisas() {
  const { token } = useAuth();
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/portal/visas`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.success) setVisas(data.visas || []);
        else setError(data.message || 'Failed to load visas');
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="dashboard-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Visa Management</h1>
              <p className="page-subtitle">Visa entries linked to your account</p>
            </div>
          </div>

          {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}
          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '14px 16px', borderRadius: '10px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {!loading && !error && visas.length === 0 && (
            <div style={{ background: '#fff', border: '1px dashed #d1d5db', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faPassport} style={{ fontSize: '28px', marginBottom: '10px', color: '#d1d5db' }} />
              <p style={{ margin: 0 }}>No visa entries found for your email.</p>
            </div>
          )}

          {visas.length > 0 && (
            <div className="table-responsive" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <table className="table mb-0" style={{ marginBottom: 0 }}>
                <thead style={{ background: '#f9fafb' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Visa ID</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Country</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Type</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Institute</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Status</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Submitted</th>
                    <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visas.map((v) => (
                    <tr key={v.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 600 }}>{v.visa_id || `#${v.id}`}</td>
                      <td style={{ padding: '14px 16px' }}>{v.country_name || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>{v.visa_type || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>{v.institute || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          ...getStatusBadgeStyle(v.visa_status),
                          display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
                          fontSize: '12px', fontWeight: 600,
                        }}>
                          {v.visa_status || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>{formatDate(v.submission_date || v.created_at)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <Link
                          to={`/edit-visa/${v.id}`}
                          className="btn-secondary btn-sm"
                          title="View"
                          style={{ textDecoration: 'none' }}
                        >
                          <FontAwesomeIcon icon={faEye} /> View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
